import { Box, Divider, makeStyles, Paper, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import React from 'react';
import { Placeholder, useTranslate } from 'react-admin';
import { useAccountInvoiceBalanceListContext } from './AccountInvoiceBalanceProvider';
import { formatDate } from '../../../../services/util/date';
import { DATE_DISPLAY_FORMAT } from '../../../../constant';
import { formatMoney } from '../../../../services/util/formatNumber';

const AccountStatementSummary = () => {
  const t = useTranslate();
  const classes = useStyles();
  const theme = useTheme();
  const mobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: accountStatementData, loading, appliedFilters } = useAccountInvoiceBalanceListContext();

  if (!accountStatementData?.items && !loading) return null;

  return (
    <Box
      component={Paper}
      sx={{
        boxShadow: 'none !important',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.grey[300]}`,
        gap: 16,
      }}
      p={{
        xs: 3,
        lg: 4,
      }}
      display="flex"
      width="100%"
      flexDirection={{
        xs: 'column',
        lg: 'row',
      }}
    >
      {loading ? (
        <Placeholder className={classes.placeholder} />
      ) : Array.isArray(accountStatementData?.items) ? (
        <Box
          display="flex"
          flexDirection={{
            xs: 'column',
            lg: 'row',
          }}
          sx={{
            gap: 16,
          }}
          width="100%"
        >
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              gap: 4,
            }}
          >
            <Typography
              variant="h4"
              style={{
                fontWeight: 600,
              }}
            >
              {t('resources.accountInvoiceBalance.statementOfAccount')}
            </Typography>
            <Typography>
              {`${formatDate(appliedFilters.fromTime, DATE_DISPLAY_FORMAT)} - ${formatDate(appliedFilters.toTime - 1, DATE_DISPLAY_FORMAT)}`}
            </Typography>
          </Box>
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              md: 'auto auto auto 1px auto',
            }}
            sx={{
              gap: 8,
            }}
            flexGrow={1}
          >
            <AmountSection
              currencyCode={accountStatementData.currencyCode}
              value={accountStatementData.openingBalance}
              label={t('resources.accountInvoiceBalance.openingBalance')}
            />
            <AmountSection
              currencyCode={accountStatementData.currencyCode}
              value={accountStatementData.invoiceAmount}
              label={t('resources.accountInvoiceBalance.invoiceAmount')}
            />
            <AmountSection
              currencyCode={accountStatementData.currencyCode}
              value={accountStatementData.amountPaid}
              label={t('resources.accountInvoiceBalance.amountPaid')}
            />

            <Box
              component={Divider}
              orientation={mobileScreen ? 'horizontal' : 'vertical'}
              variant="fullWidth"
              sx={{
                backgroundColor: `${theme.palette.primary[500]} !important`,
              }}
            />
            <AmountSection
              currencyCode={accountStatementData.currencyCode}
              value={accountStatementData.balanceDue}
              label={t('resources.accountInvoiceBalance.balanceDue')}
              highlight
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default AccountStatementSummary;

const AmountSection = ({ label, value, highlight, currencyCode }) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      flexDirection="column"
      component={Paper}
      sx={{
        boxShadow: 'none !important',
        background: `${highlight ? theme.palette.primary[200] : theme.palette.primary[100]} !important`,
        gap: 4,
      }}
      p={3}
    >
      <Typography
        style={{
          fontSize: 15,
          fontWeight: highlight ? 600 : 400,
        }}
      >
        {label}
      </Typography>
      <Typography
        style={{
          fontSize: 15,
          fontWeight: 600,
        }}
      >
        {formatMoney(value, currencyCode)}
      </Typography>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  placeholder: {
    minHeight: 120,
    borderRadius: 8,
    width: '100%',
  },
}));
